import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { LoginRequest } from "../types/api";

interface LoginProps {
  onLoginSuccess: () => void;
  onRequires2FASetup: () => void;
}

export const Login: React.FC<LoginProps> = ({
  onLoginSuccess,
  onRequires2FASetup,
}) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginRequest>({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpToken, setTotpToken] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.login({
        ...credentials,
        totp_token: requires2FA ? totpToken : undefined,
      });

      if (response.requires_two_fa) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (response.requires_2fa_setup) {
        onRequires2FASetup();
        return;
      }

      if (response.access_token) {
        onLoginSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-12 px-4">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-8 pt-8 pb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Sign In</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
                required
                disabled={requires2FA}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                  requires2FA
                    ? "bg-gray-100 cursor-not-allowed"
                    : "bg-white border-gray-300"
                }`}
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                required
                disabled={requires2FA}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                  requires2FA
                    ? "bg-gray-100 cursor-not-allowed"
                    : "bg-white border-gray-300"
                }`}
              />
              {!requires2FA && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
            {requires2FA && (
              <div>
                <label
                  htmlFor="totpToken"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Two-Factor Code
                </label>
                <input
                  type="text"
                  id="totpToken"
                  value={totpToken}
                  onChange={(e) =>
                    setTotpToken(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                  maxLength={6}
                  placeholder="000000"
                  autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-xl tracking-widest focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Enter the 6-digit code from your authenticator app
                </p>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    Lost access to your authenticator device? Contact your admin
                    or supervisor directly to reset your 2FA.
                  </p>
                </div>
              </div>
            )}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={loading || (requires2FA && totpToken.length !== 6)}
                className="flex-1 py-3 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              {requires2FA && (
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setTotpToken("");
                    setError(null);
                  }}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-lg transition-all"
                >
                  Back
                </button>
              )}
            </div>
          </form>
        </div>
        <div className="border-t border-gray-100 bg-gray-50 px-8 py-4 text-center text-sm text-gray-600">
          New here?{" "}
          <Link to="/about" className="text-primary font-medium hover:underline">
            Read about the system
          </Link>{" "}
          — accounts are issued by invitation.
        </div>
      </div>
    </div>
  );
};
